import api from './api';

/**
 * Connection Service
 * Handles professional networking and connections
 */
export const connectionService = {
  // ==================== Connection Management ====================
  
  /**
   * Get all connections
   */
  async getConnections(userId, params) {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/connections/user/${userId}?${queryString}`);
  },

  /**
   * Get connection details
   */
  async getConnectionDetails(id) {
    return api.get(`/connections/${id}`);
  },

  /**
   * Send connection request
   */
  async sendConnectionRequest(data) {
    return api.post('/connections/request', data);
  },

  /**
   * Accept connection request
   */
  async acceptConnectionRequest(userId, connectionId) {
    return api.put(`/connections/${userId}/${connectionId}/accept`);
  },

  /**
   * Reject connection request
   */
  async rejectConnectionRequest(userId, connectionId, reason = "No Reason") {
    return api.put(`/connections/${userId}/${connectionId}/reject`, { reason });
  },

  /**
   * Remove connection
   */
  async removeConnection(userId, connectionId) {
    const body = {
      reason: "User requested removal"
    };
    return api.put(`/connections/${userId}/${connectionId}/REMOVE`, body);
  },

  /**
   * Block connection
   */
  async blockConnection(userId, connectionId) {
    const body = {
      reason: "User requested removal"
    };
    return api.put(`/connections/${userId}/${connectionId}/BLOCK`, body);
  },

  /**
   * Cancel connection
   */
  async cancelConnection(userId, connectionId) {
    const body = {
      reason: "User requested cancellation"
    };
    return api.put(`/connections/${userId}/${connectionId}/CANCEL`, body);
  },

  // ==================== Connection Requests ====================
  
  /**
   * Get pending connection requests
   */
  async getPendingRequests(userId) {
    return api.get(`/connections/requests/pending/user/${userId}`);
  },

  /**
   * Get sent connection requests
   */
  async getSentRequests(userId) {
    return api.get(`/connections/requests/sent/user/${userId}`);
  },

  // ==================== Connection Stats ====================
  
  /**
   * Get connection statistics
   */
  async getConnectionStats() {
    return api.get('/connections/stats');
  },

  /**
   * Get network growth data
   */
  async getNetworkGrowth(params) {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/connections/stats/growth?${queryString}`);
  },

  // ==================== Connection Suggestions ====================
  
  /**
   * Get connection suggestions
   */
  async getConnectionSuggestions(params) {
    const userId = localStorage.getItem('userId');
    if (!userId) return Promise.reject('No user ID');
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/connections/suggestions/${userId}?${queryString}`);
  },

  /**
   * Discover non-connected users with pagination
   */
  async discoverUsers(userId, params) {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/connections/discover/${userId}?${queryString}`);
  },

  async discoverUsersByRole(userId) {
    return api.get(`/connections/discover-by-role/${userId}`);
  },

  async sendMessageToConnection(userId, connectionId, message) {
    return api.post(`/connections/${userId}/${connectionId}/send-message`, { message });
  },

  async getConnectionsForUser(userId) {
    return api.get(`/connections/user/${userId}?page=0&size=200`);
  },

  // ==================== Connection Notes & Tags ====================
  
  /**
   * Add note to connection
   */
  async addConnectionNote(connectionId, note) {
    return api.post(`/connections/${connectionId}/notes`, { note });
  },

  /**
   * Update connection tags
   */
  async updateConnectionTags(connectionId, tags) {
    return api.put(`/connections/${connectionId}/tags`, { tags });
  },

  /**
   * Share or unshare portfolio
   */
  async sharePortfolio(userId, connectionId, portfolioSharedStatus) {
    const queryString = new URLSearchParams({ portfolioSharedStatus }).toString();
    return api.put(`/connections/${userId}/${connectionId}/portfolio-shared-status?${queryString}`);
  },
};

export default connectionService;
